/**
 * Discovered-contacts utilities (TD-2106, Contact Enrichment Stream B).
 *
 * Defensive client-side dedup (AC14): discovery results are ephemeral and may
 * overlap the stored CREFO contacts already shown on the Key Contacts tab. We
 * drop any discovered contact that matches a stored one so the same person is
 * not rendered twice. The stored record always wins (it is the canonical row).
 *
 * Matching rules:
 *  - Primary: normalised firstName + lastName. When BOTH records carry a
 *    companyId, that must also match (guards the rare cross-company collision;
 *    in practice the tab is single-company so companyId is usually absent).
 *  - Secondary: a non-empty, normalised linkedin_url match (catches people
 *    listed under abbreviated/alternate names).
 *
 * Field names are not yet final BE-side (TD-2083), so we read both snake_case
 * (UI shape) and camelCase defensively.
 */

import { levelForSeniority } from './ManagementLevelChecklist';

const str = (v) => {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
};

const norm = (v) => str(v).trim().replace(/\s+/g, ' ').toLowerCase();

const firstName = (c) => norm(c?.first_name ?? c?.firstName);
const lastName = (c) => norm(c?.last_name ?? c?.lastName);
const companyId = (c) => norm(c?.companyId ?? c?.company_id);
const linkedin = (c) => norm(c?.linkedin_url ?? c?.linkedinUrl);

const nameKey = (c) => `${firstName(c)} ${lastName(c)}`;

/**
 * True when the discovered contact refers to the same person as the stored one.
 */
const isSamePerson = (discovered, storedContact) => {
  const linkedinUrl = linkedin(discovered);
  if (linkedinUrl && linkedinUrl === linkedin(storedContact)) {
    return true;
  }

  // Name match (both names must be present to be meaningful).
  const fn = firstName(discovered);
  const ln = lastName(discovered);
  if (!fn || !ln) return false;
  if (nameKey(discovered) !== nameKey(storedContact)) return false;

  // Only enforce companyId when both sides supply one.
  const dCompany = companyId(discovered);
  const sCompany = companyId(storedContact);
  if (dCompany && sCompany && dCompany !== sCompany) return false;

  return true;
};

/**
 * Returns the discovered contacts that do NOT collide with the stored set,
 * preserving order. Never mutates either input; tolerates missing arrays.
 *
 * @param {Array<Object>} storedContacts - canonical CREFO contacts already shown
 * @param {Array<Object>} discoveredContacts - ephemeral discovery results
 * @returns {Array<Object>} discovered contacts safe to render
 */
export const dedupeDiscoveredContacts = (storedContacts, discoveredContacts) => {
  const discovered = Array.isArray(discoveredContacts) ? discoveredContacts : [];
  const stored = Array.isArray(storedContacts) ? storedContacts : [];
  if (!stored.length) return [...discovered];

  return discovered.filter(
    (contact) => !stored.some((storedContact) => isSamePerson(contact, storedContact))
  );
};

/**
 * TD-2497: filter discovered contacts to those whose Surfe seniority maps to a
 * currently-selected management level.
 *
 * The Discover panel's management-level checklist doubles as the display filter
 * for the discovered section: deselecting a level hides its people, re-selecting
 * shows them again from the retained results (no re-discovery, no cost). A search
 * auto-checks the levels of whoever came back (see DiscoverContactsPanel), so
 * title-only discoveries display too.
 *
 * A contact with a free-text / unknown seniority (no `manager_function` in the
 * Surfe enum — the BE falls back to job title) maps to no level and is always
 * shown: the level checklist can't address it, so it must not silently vanish.
 *
 * @param {Array<Object>} contacts - deduped discovered contacts
 * @param {Array<string>} selectedLevels - management-level values currently ticked
 * @returns {Array<Object>} the subset to render (order preserved)
 */
export const filterDiscoveredBySelectedLevels = (contacts, selectedLevels) => {
  const list = Array.isArray(contacts) ? contacts : [];
  const levels = Array.isArray(selectedLevels) ? selectedLevels : [];
  return list.filter((contact) => {
    const level = levelForSeniority(contact?.manager_function);
    return level ? levels.includes(level) : true;
  });
};
